<template>
  <RevContainer>
    <NoSales v-if="!hasSales && hasFetchedOnce"></NoSales>
    <div v-else>
      <template v-if="!hasFetchedOnce">
        <RevSkeleton
          class="mb-32"
          height="90px"
          shape="rectangle"
          width="100%"
        />
        <RevSkeleton
          v-for="item in 4"
          :key="item"
          class="mb-52"
          height="290px"
          shape="rectangle"
          width="100%"
        />
      </template>
      <template v-else>
        <SaleItem
          v-for="sale in sales"
          :key="sale.id"
          class="mb-52"
          :item="sale"
          @reload-sales="reloadSales"
        />
        <ClientOnly>
          <LoadMore v-if="canFetchMore" :is-loading @load-more="fetchMore" />
        </ClientOnly>
      </template>
    </div>
    <Intercom :hide-launcher="true" :manual="true" />
  </RevContainer>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import type { HttpApiError } from '@backmarket/http-api'
import Intercom from '@backmarket/nuxt-layer-care-platform/intercom/components/Intercom.vue'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useSWRAsyncData } from '@backmarket/nuxt-module-http-v2/useSWRAsyncData'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevContainer } from '@ds/components/Container'
import { RevSkeleton } from '@ds/components/Skeleton'

import { LoadMore } from '#components'

import {
  type SaleItemType,
  mySalesAdapter,
} from '~/scopes/buyback/api/getCustomerSales/mySalesAdapter'

import translations from './MySales.translations'
import NoSales from './components/NoSales/NoSales.vue'
import SaleItem from './components/SaleItem/SaleItem.vue'

const i18n = useI18n()
const logger = useLogger()
const { openErrorToast, openNeutralToast } = useTheToast()
const $httpFetch = useHttpFetch()

const isLoading = ref(false)

const { data, hasFetchedOnce } = useSWRAsyncData('mySales', () =>
  $httpFetch('/bm/buyback/v1/customer/orders', {
    query: { page: 1 },
    onResponseError: ({ response }) => {
      if (response) {
        logger.error('[BUYBACK][API][MYSALES] Error getting customer sales', {
          // eslint-disable-next-line no-underscore-dangle
          error: response._data,
          owners: ['bot-squad-circularity-order-lifecycle-front'],
        })
      }
    },
  }),
)

const overrideSales = ref<SaleItemType[] | undefined>(undefined)
const sales = computed(() => {
  if (typeof overrideSales.value !== 'undefined') return overrideSales.value

  return data.value ? mySalesAdapter(data.value).items : []
})

const overrideNext = ref<string | null | undefined>(undefined)
const next = computed(() => {
  if (typeof overrideNext.value !== 'undefined') return overrideNext.value

  return data.value ? mySalesAdapter(data.value).next : null
})

const getHasMissingShippingLabel = (items: SaleItemType[]) => {
  return Boolean(items?.some(({ timeline }) => !!timeline?.steps?.[0]?.current))
}

watch(
  data,
  (newData) => {
    // watching data and not sales to only do it for the first page here
    // and only client side
    if (!newData || !import.meta.client) {
      return
    }

    // Show info toast if at least one trade-in hasn't its shipping label yet
    if (getHasMissingShippingLabel(mySalesAdapter(newData).items)) {
      openNeutralToast({
        title: i18n(translations.toastTitle),
        content: i18n(translations.toastContent),
        name: 'mysales-info-toast',
      })
    }
  },
  { immediate: true },
)

const canFetchMore = computed(() => next.value)
const hasSales = computed(() => sales.value?.length > 0)

async function reloadSales(id: string) {
  const index = sales.value.findIndex((sale) => sale.id === id)
  const page = index === -1 ? null : Math.ceil((index + 1) / 10)
  if (page) {
    try {
      const response = await $httpFetch('/bm/buyback/v1/customer/orders', {
        query: { page },
      })

      if (response) {
        const myNewSalesData = mySalesAdapter(response)
        const myNewSalesDataIndex = index - 10 * (page - 1)
        sales.value[index] = myNewSalesData.items[myNewSalesDataIndex]
      }
    } catch (err) {
      openErrorToast()

      logger.error('[BUYBACK][API][MYSALES] Error reloading customer sales', {
        error: err as HttpApiError,
        owners: ['bot-squad-circularity-order-lifecycle-front'],
      })
    }
  }
}

async function fetchMore() {
  isLoading.value = true

  if (next.value === null) {
    return
  }

  try {
    const response = await $httpFetch('/bm/buyback/v1/customer/orders', {
      query: {
        page: parseInt(next.value, 10),
      },
    })

    if (response) {
      const myNewSalesData = mySalesAdapter(response)
      overrideNext.value = myNewSalesData.next

      const existingItems = overrideSales.value ?? sales.value
      const allItems = [...existingItems, ...myNewSalesData.items]
      overrideSales.value = allItems

      // Show info toast if at least one trade-in hasn't its shipping label yet
      if (getHasMissingShippingLabel(myNewSalesData.items)) {
        openNeutralToast({
          title: i18n(translations.toastTitle),
          content: i18n(translations.toastContent),
          name: 'mysales-info-toast',
        })
      }
    }
  } catch (err) {
    logger.error('[BUYBACK][API][MYSALES] Error getting more customer sales', {
      error: err as HttpApiError,
      owners: ['bot-squad-circularity-order-lifecycle-front'],
    })
  }

  isLoading.value = false
}
</script>
